/**
 * The collapsible full-scope selectable grid.
 *
 * Renders every HUMAN plan item at once, grouped jurisdiction → subject
 * (proper headings, not labels). Tiles are multi-selectable (click +
 * shift-click range over the eligible/ungrouped tiles). Already-grouped
 * tiles show a coloured membership chip and are not selectable. The
 * toolbar's "Create assignment" turns the current selection into a new
 * assignment.
 *
 * Only needed while creating assignments — collapsible, and auto-collapsed
 * after the first assignment is created (the analyst re-expands to add
 * more). Once collapsed, each assignment card still shows its own scope
 * subset, so the full grid is a reference rather than a permanent fixture.
 */

import {
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';

import type { ResearchPlanItemForPanel } from '@/types/research-plan';
import { AddJurisdictionControl } from './AddJurisdictionControl';
import { useNpAssignmentsContext } from './NpAssignmentsContext';
import { templateTileId } from './templateTiles';

function jurisdictionKey(item: ResearchPlanItemForPanel): string {
  return item.jurisdiction_name || 'Unspecified jurisdiction';
}

function subjectKey(item: ResearchPlanItemForPanel): string {
  return item.subject_name || 'Unspecified subject';
}

interface GroupedScope {
  jurisdiction: string;
  subjects: { subject: string; items: ResearchPlanItemForPanel[] }[];
}

/** jurisdiction → subject → items, preserving plan order. */
function groupScope(items: ResearchPlanItemForPanel[]): GroupedScope[] {
  const out: GroupedScope[] = [];
  for (const item of items) {
    const jKey = jurisdictionKey(item);
    const sKey = subjectKey(item);
    let jGroup = out.find((g) => g.jurisdiction === jKey);
    if (!jGroup) {
      jGroup = { jurisdiction: jKey, subjects: [] };
      out.push(jGroup);
    }
    let sGroup = jGroup.subjects.find((s) => s.subject === sKey);
    if (!sGroup) {
      sGroup = { subject: sKey, items: [] };
      jGroup.subjects.push(sGroup);
    }
    sGroup.items.push(item);
  }
  return out;
}

export function ScopeGrid() {
  const {
    humanItems,
    assignments,
    gridSelectionIds,
    setGridSelectionIds,
    gridCollapsed,
    setGridCollapsed,
    colorForAssignment,
    createFromSelection,
    selectAssignment,
    source,
  } = useNpAssignmentsContext();

  // Map tile id → assignment id (membership), for chips + disabling. Automated
  // (plan-item) membership keys by planItemId; legacy (template) membership
  // keys by the synthesized slug::subject::jurisdiction tile id (there is no
  // plan-item id) — the template tile source builds tile ids the same way. The
  // jurisdiction code rides `dimensions.jurisdiction` for both kinds.
  const assignmentByItem = new Map<string, string>();
  for (const a of assignments) {
    for (const it of a.items) {
      const jCode =
        typeof it.dimensions?.jurisdiction === 'string' ? it.dimensions.jurisdiction : null;
      const key =
        it.kind === 'template_component'
          ? templateTileId(it.scopeComponentSlug, it.subjectId, jCode)
          : it.planItemId;
      if (key) {
        assignmentByItem.set(key, a.id);
      }
    }
  }

  // Flat ordered list of eligible (ungrouped) ids — the basis for
  // shift-click range selection.
  const orderedEligibleIds = humanItems.map((i) => i.id).filter((id) => !assignmentByItem.has(id));

  const selected = new Set(gridSelectionIds);
  let lastClickedId: string | null = null;

  const toggle = (id: string, shiftKey: boolean) => {
    if (assignmentByItem.has(id)) {
      return; // already grouped — not selectable
    }
    const next = new Set(selected);
    if (shiftKey && lastClickedId) {
      const a = orderedEligibleIds.indexOf(lastClickedId);
      const b = orderedEligibleIds.indexOf(id);
      if (a !== -1 && b !== -1) {
        const [lo, hi] = a < b ? [a, b] : [b, a];
        for (let i = lo; i <= hi; i++) {
          next.add(orderedEligibleIds[i]);
        }
      } else {
        next.add(id);
      }
    } else if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    lastClickedId = id;
    setGridSelectionIds([...next]);
  };

  const handleCreate = async () => {
    if (gridSelectionIds.length === 0) {
      return;
    }
    const created = await createFromSelection(gridSelectionIds);
    setGridSelectionIds([]);
    setGridCollapsed(true);
    if (created) {
      // Land on the new assignment with the aside open, ready to shortlist NPs.
      selectAssignment(created.id);
    }
  };

  const grouped = groupScope(humanItems);
  const eligibleCount = orderedEligibleIds.length;

  return (
    <Card shadow="sm" padding="lg" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Button
              variant="subtle"
              size="compact-sm"
              px={4}
              onClick={() => setGridCollapsed(!gridCollapsed)}
              aria-label={gridCollapsed ? 'Expand scope' : 'Collapse scope'}
            >
              {gridCollapsed ? <IconChevronRight size={18} /> : <IconChevronDown size={18} />}
            </Button>
            <Stack gap={0}>
              <Title order={4}>Project scope</Title>
              <Text size="xs" c="dimmed">
                {humanItems.length} item(s) needing a Network Partner · {eligibleCount} ungrouped
              </Text>
            </Stack>
          </Group>
          <Group gap="xs">
            {source === 'template_components' && <AddJurisdictionControl />}
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setGridSelectionIds([...orderedEligibleIds])}
              disabled={eligibleCount === 0 || gridSelectionIds.length === eligibleCount}
            >
              Select all
            </Button>
            {gridSelectionIds.length > 0 && (
              <Button
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => setGridSelectionIds([])}
              >
                Clear ({gridSelectionIds.length})
              </Button>
            )}
            <Button
              size="xs"
              onClick={() => void handleCreate()}
              disabled={gridSelectionIds.length === 0}
              data-testid="create-assignment-btn"
            >
              Create assignment
            </Button>
          </Group>
        </Group>

        <Collapse in={!gridCollapsed}>
          <Stack gap="md" mt="xs">
            {grouped.length === 0 && (
              <Text size="sm" c="dimmed">
                This report has no HUMAN plan items — no assignments needed.
              </Text>
            )}
            {grouped.map((jGroup) => (
              <Stack key={jGroup.jurisdiction} gap="xs">
                <Divider
                  label={
                    <Title order={5} c="dimmed">
                      {jGroup.jurisdiction}
                    </Title>
                  }
                  labelPosition="left"
                />
                {jGroup.subjects.map((sGroup) => (
                  <Stack key={sGroup.subject} gap={6} pl="sm">
                    <Text size="sm" fw={600}>
                      {sGroup.subject}
                    </Text>
                    <Stack gap={4}>
                      {sGroup.items.map((item) => {
                        const memberOf = assignmentByItem.get(item.id);
                        return (
                          <PlanItemTile
                            key={item.id}
                            item={item}
                            selected={selected.has(item.id)}
                            membershipColor={memberOf ? colorForAssignment(memberOf) : undefined}
                            membershipLabel={
                              memberOf
                                ? `A${assignments.findIndex((a) => a.id === memberOf) + 1}`
                                : undefined
                            }
                            onToggle={(shiftKey) => toggle(item.id, shiftKey)}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}

interface PlanItemTileProps {
  item: ResearchPlanItemForPanel;
  selected: boolean;
  membershipColor?: string;
  membershipLabel?: string;
  onToggle: (shiftKey: boolean) => void;
}

function PlanItemTile({
  item,
  selected,
  membershipColor,
  membershipLabel,
  onToggle,
}: Readonly<PlanItemTileProps>) {
  const grouped = Boolean(membershipLabel);
  return (
    <Card
      withBorder
      padding="xs"
      radius="sm"
      data-testid={`scope-tile-${item.id}`}
      style={{
        cursor: grouped ? 'default' : 'pointer',
        opacity: grouped ? 0.65 : 1,
        borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
      }}
      onClick={(e) => {
        if (!grouped) {
          onToggle(e.shiftKey);
        }
      }}
    >
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <Checkbox
            checked={selected}
            readOnly
            disabled={grouped}
            tabIndex={-1}
            aria-label={`Select ${item.scope_component_slug}`}
          />
          <Text size="sm" truncate>
            {item.scope_component_slug}
          </Text>
        </Group>
        {membershipLabel && (
          <Badge color={membershipColor} variant="light" size="sm">
            {membershipLabel}
          </Badge>
        )}
      </Group>
    </Card>
  );
}
